// Emails (Block J, UI-22c, 2026-10-11): the inbox on the v2 kit, after mockups/emails.html.
// Folders | list | reading pane on a wide screen; list then reading pane on a narrow one; on a
// phone the list, and the email opens full screen (addendum 6). This file keeps the data and the
// handlers; the pieces are in EmailParts.jsx and the look in emails.css (tokens only).
//
// Rules this page keeps (each with a test in EmailInbox.test.js):
//   - a failed load is an error in the server's own words, never an empty folder
//   - nothing says "done" before the server has said so
//   - deleting an Aidey folder waits behind Undo (decision 7); an email delete is committed at
//     once and Undo restores it from Trash, because the provider does the delete
//   - email calendar suggestions are the mailbox owner's alone (decision 33): this page shows only
//     the signed-in person's own mailboxes; the server scopes every read to them
//   - Folders (2026-10-21): each account's OWN folders (Gmail labels, Outlook and IMAP folders) sit
//     beside Aidey's. Deleting an Outlook or IMAP folder deletes its emails, so its confirm names
//     how many and Cancel is the default; Gmail's says the emails stay. A move has an Undo.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';   // Block 233
import { showToast as showSharedToast } from '../../utils/toast';   // fix(toast): the shared toast
import { formatDateTime } from '../../utils/formatting';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import {
  PageLayout, PageHeader, Button, Banner, EmptyState, ErrorState, Skeleton, SearchInput, SegmentedControl,
  Checkbox, SidePanel, Sheet, ConfirmDialog, MoveToSheet, OverflowMenu, StatusPill, useBreakpoint, useDelayedDelete, HeaderButton,
} from '../../ui';
import {
  FolderNav, TriageChips, EmailRow, SpamRow, AideyDraftRow, ContactCard, SenderAutomation, AideyDraftCard,
  AideyDraftPending, ReplyComposer, ComposeModal, SenderLine, accountMeta, connectionLabel, senderName, htmlToText,
  AideySummaryCard, mailboxFolderKey,
  safeUnsubscribeUrl,
} from './EmailParts';
import './emails.css';
import { bannerText, bannerAction, fixLabel, reconnectMailbox } from '../../utils/mailboxHealth';   // 2026-10-21

// ── Helpers ───────────────────────────────────────────────────────────────────

// An email's time in the list: today as a time, this week as a day and time, else the date.
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diffH = (now - d) / 3600000;
  if (diffH < 24 && d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffH >= 0 && diffH < 168) {
    return d.toLocaleDateString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const keyOf = (e) => `${e.provider}-${e.id}`;
const gotoSettings = (section) => window.dispatchEvent(new CustomEvent('aidey:goto-settings', { detail: section }));
const MAIL_PROVIDERS = ['gmail', 'outlook', 'imap'];
const FOLDER_LABEL = { inbox: 'Inbox', starred: 'Starred', sent: 'Sent', drafts: 'Drafts', spam: 'Spam', deleted: 'Deleted', mailbox: 'Folder' };
// An IMAP email listed from one of its folders: the read, star, junk and delete routes find an IMAP
// uid in the Inbox, so those actions are not offered on it (a uid in another folder is another email).
const imapOutsideInbox = (e) => !!e && e.provider === 'imap' && e.folder === 'mailbox';
// Archive takes an email out of the Inbox (lib/mailMoves), so it is offered for Inbox email only.
const archivable = (e, view) => !!e && MAIL_PROVIDERS.includes(e.provider) && (view === 'inbox' || view === 'starred') && e.folder !== 'mailbox' && e.folder !== 'sent';
const ARCHIVE_DEST = { id: 'archive', label: 'Archive', icon: '🗄️', color: 'pastel-mint', hint: 'In your mailbox', kind: 'archive' };
// Back to the Inbox from one of the mailbox's folders (2026-10-23). Each provider names it its own way.
const INBOX_DEST = { id: 'inbox', label: 'Inbox', icon: '📥', color: 'pastel-blue', hint: 'In your mailbox', kind: 'inbox' };
const inboxFolderOf = (provider) => ({ id: provider === 'outlook' ? 'inbox' : 'INBOX', name: 'Inbox' });
// A move from a mailbox folder says which folder it is leaving.
const moveSource = (e) => (e.folder === 'mailbox' && e.mailboxFolderId != null
  ? (e.provider === 'imap' ? { sourcePath: e.mailboxPath || e.mailboxFolderId } : { sourceFolderId: e.mailboxFolderId })
  : {});

// The email body, sanitised, in a sandboxed frame. The frame cannot read the page's tokens, so
// its own colours are written here (the ink and a link colour, the same values as tokens.css).
const frameDoc = (html) => `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;font-size:15px;line-height:1.6;color:#1e1b3a;padding:4px 2px;margin:0;overflow-wrap:anywhere}a{color:#1e1b3a}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${DOMPurify.sanitize(html || '<p>No content</p>')}</body></html>`;

// Three panes when the card is wide enough to read in, else the list beside the email.
function useWidth(ref) {
  const [w, setW] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function EmailInbox({ setFilter, onAskAidey }) {
  const { activeWorkspaceId, workspaces } = useCurrentUser() || {};
  const isPersonal = (workspaces || []).find((w) => w.id === activeWorkspaceId)?.type === 'personal';
  const phone = useBreakpoint() === 'phone';
  const shellRef = useRef(null);
  const shellWidth = useWidth(shellRef);
  const layout = phone ? 'one' : (shellWidth == null || shellWidth >= 880 ? 'three' : 'two');

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState([]);
  const [connectionChecked, setConnectionChecked] = useState(false);
  const [connectionsError, setConnectionsError] = useState(null);
  // Fresh-eyes item 13: { unread, total, account } of the INBOX, from the last unsearched inbox load.
  // The server counts the whole inbox (routes/email/inbox inboxUnreadCount); the header and the Inbox
  // folder both show this one number, for the account it was counted for.
  const [inboxCounts, setInboxCounts] = useState(null);

  const [selected, setSelected] = useState(null);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [activeProvider, setActiveProvider] = useState(null);
  const [activeConnectionId, setActiveConnectionId] = useState(null);
  const [readFilter, setReadFilter] = useState('all');   // all | unread | starred (this page's emails)

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const emailListRef = useRef(null);

  const [triageFilter, setTriageFilter] = useState(null);

  const [showReply, setShowReply] = useState(false);
  const [replying, setReplying] = useState(false);
  const [compose, setCompose] = useState(null);   // null | { title?, subject?, body?, fromId?, note? }
  const [composing, setComposing] = useState(false);
  // fix(toast): the shared toast. One at a time here: a new one replaces the last (an error
  // after a delete replaces its Undo), as this screen's own toast did.
  const toastElRef = useRef(null);
  const undoRef = useRef(null);
  const closeToast = useCallback(() => { toastElRef.current?.remove(); toastElRef.current = null; }, []);
  const [showFolders, setShowFolders] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingDeleteItems, setPendingDeleteItems] = useState(null);
  const deleteTimeoutRef = useRef(null);
  const pendingDeleteRef = useRef(new Set());
  const restoreHandlesRef = useRef([]);   // restore-from-trash handles for the current undo window
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [folderAssignments, setFolderAssignments] = useState(null);
  const [folderError, setFolderError] = useState(null);
  const [folderReload, setFolderReload] = useState(0);
  const [moveOpen, setMoveOpen] = useState(false);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  // IMAP mailboxes with no Trash folder: the server deleted nothing and asks first (Carla, 2026-10-21).
  const [noTrash, setNoTrash] = useState(null);   // { emails, question } while the question is open
  // Email Phase 1 (2026-10-21): the email whose sender is about to be blocked (the confirm is open).
  const [blockFor, setBlockFor] = useState(null);
  // Email Phase 2 (2026-10-21): snooze and "remind if no reply" choices, and the clean-up panel.
  const [snoozeFor, setSnoozeFor] = useState(null);
  const [remindFor, setRemindFor] = useState(null);
  const [cleanup, setCleanup] = useState(null);   // null closed; { status, list, error } open

  const [replyInitialBody, setReplyInitialBody] = useState('');
  const [replyInitialSubject, setReplyInitialSubject] = useState('');
  const [dismissedDrafts, setDismissedDrafts] = useState(new Set()); // emailIds dismissed by user
  const [aideyDrafts, setAideyDrafts] = useState([]);
  const [aideyDraftsError, setAideyDraftsError] = useState(null);
  const [automationConfigs, setAutomationConfigs] = useState({});
  const [spamEmails, setSpamEmails] = useState([]);
  const [spamCount, setSpamCount] = useState(0);
  const [spamWeek, setSpamWeek] = useState(0);   // 2026-10-22: how many Aidey moved this week
  const [spamError, setSpamError] = useState(null);
  const [spamFailed, setSpamFailed] = useState([]);   // 2026-10-23: mailboxes whose Junk did not load
  const [spamLoading, setSpamLoading] = useState(false);
  const [failedConnections, setFailedConnections] = useState([]);
  // Folders (2026-10-21): each account's own folders, the one open, the email being moved, the folder
  // whose delete is being confirmed.
  const [mailboxes, setMailboxes] = useState([]);
  const [mailboxesError, setMailboxesError] = useState(null);
  const [activeMailboxFolder, setActiveMailboxFolder] = useState(null);   // { connectionId, provider, id, name }
  const activeMailboxFolderRef = useRef(null);
  const [moveEmail, setMoveEmail] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(null);   // { mailbox, folder }

  const activeProviderRef = useRef(null);
  const activeConnectionIdRef = useRef(null);
  const currentPageRef = useRef(1);
  const searchQueryRef = useRef('');
  const activeFolderRef = useRef('inbox');

  // ── Toast ──────────────────────────────────────────────────────────────────

  // showUndo keeps it open for the 8s undo window, with an Undo action.
  const showToast = useCallback((msg, type = 'success', showUndo = false) => {
    closeToast();
    toastElRef.current = showSharedToast(msg, showUndo
      ? { type, ms: 8000, action: { label: 'Undo', onClick: () => undoRef.current?.() } }
      : { type });
  }, [closeToast]);

  // ── Data fetching ──────────────────────────────────────────────────────────

  // A failed connections load is not "no accounts connected": it is an error, with a retry.
  const fetchConnections = useCallback(async () => {
    setConnectionsError(null);
    try {
      const res = await api('/api/connections/email');
      const data = await res.json();
      setConnections(data.connections || []);
    } catch (err) {
      setConnectionsError(messageFromError(err, 'Your email accounts could not be loaded.'));
    } finally {
      setConnectionChecked(true);
    }
  }, []);

  const fetchEmails = useCallback(async (page = 1, q = '', provider = null, folder = null, connectionId = null) => {
    const resolvedFolder = folder !== null ? folder : (activeFolderRef.current || 'inbox');
    let apiPath = '/api/email/inbox';
    const mbFolder = resolvedFolder === 'mailbox' ? activeMailboxFolderRef.current : null;
    if (mbFolder) apiPath = '/api/email/mailbox-folders/emails';
    else if (resolvedFolder === 'sent') apiPath = '/api/email/sent';
    else if (resolvedFolder === 'drafts') apiPath = '/api/email/drafts';
    else if (resolvedFolder === 'deleted') apiPath = '/api/email/deleted';

    setLoading(true);
    setError(null);
    try {
      const emailLimit = window.innerWidth <= 767 ? 5 : 8;
      const params = new URLSearchParams({ page: String(page), limit: String(emailLimit) });
      if (q) params.set('q', q);
      const resolvedConnectionId = connectionId !== null ? connectionId : activeConnectionIdRef.current;
      if (mbFolder) {
        params.set('connectionId', String(mbFolder.connectionId));
        params.set('folderId', String(mbFolder.id));
      } else {
        if (provider) params.set('provider', provider);
        if (resolvedConnectionId) params.set('connectionId', String(resolvedConnectionId));
      }
      const res = await api(`${apiPath}?${params}`);
      const data = await res.json();
      const fetched = data.emails || [];
      const pending = pendingDeleteRef.current;
      setEmails(pending.size ? fetched.filter((e) => !pending.has(keyOf(e))) : fetched);
      setPagination(data.pagination || null);
      setCurrentPage(page);
      setFailedConnections(data.failedConnections || []);
      // A search narrows totalUnread to what matched, so only an unsearched load says what the Inbox holds.
      if (data.totalUnread != null && !q) {
        const account = resolvedConnectionId ? `imap-${resolvedConnectionId}` : (provider || 'all');
        setInboxCounts({ unread: data.totalUnread, total: data.pagination?.total ?? fetched.length, account });
        // The sidebar's badge is every account's Inbox, so only the all-accounts count may set it.
        if (account === 'all') window.dispatchEvent(new CustomEvent('aidey:emailUnreadCount', { detail: { count: data.totalUnread } }));
      }
    } catch (err) {
      setError(messageFromError(err, 'Your emails could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAideyDrafts = useCallback(async () => {
    setAideyDraftsError(null);
    try {
      const res = await api('/api/email/aidey-drafts');
      const data = await res.json();
      setAideyDrafts(data.emails || []);
    } catch (err) {
      setAideyDraftsError(messageFromError(err, "Aidey's drafts could not be loaded."));
    }
  }, []);

  const fetchSpamEmails = useCallback(async () => {
    setSpamError(null);
    setSpamLoading(true);
    try {
      const res = await api('/api/email/spam');
      const data = await res.json();
      const list = data.emails || [];
      setSpamEmails(list);
      setSpamWeek(Number(data?.movedThisWeek) || 0);
      setSpamFailed(Array.isArray(data?.failedMailboxes) ? data.failedMailboxes : []);
      setSpamCount(list.filter((e) => !e.spamActioned).length);
    } catch (err) {
      setSpamError(messageFromError(err, 'Your spam could not be loaded.'));
    } finally {
      setSpamLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
    api('/api/email/automation/config').then((r) => r.json()).then((d) => {
      if (d?.configs) setAutomationConfigs(Object.fromEntries(d.configs.map((c) => [c.intent_type, c])));
    }).catch(() => { /* no config: the automation controls stay hidden, as with none set up */ });
  }, [fetchConnections]);

  useEffect(() => {
    if (connections.length > 0) {
      fetchEmails();
    }
  }, [connections, fetchEmails]);

  // Keep refs in sync so the poll closure always reads current state
  useEffect(() => { activeProviderRef.current = activeProvider; }, [activeProvider]);
  useEffect(() => { activeConnectionIdRef.current = activeConnectionId; }, [activeConnectionId]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => { activeFolderRef.current = activeFolder; }, [activeFolder]);

  // Poll every 2 minutes; uses refs so it respects current provider/page/search/folder/connectionId
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEmails(currentPageRef.current, searchQueryRef.current, activeProviderRef.current, activeFolderRef.current, activeConnectionIdRef.current);
      if (activeFolderRef.current === 'drafts') fetchAideyDrafts();
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchEmails, fetchAideyDrafts]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api('/api/email/folders');
      const data = await res.json();
      setFolders(data.folders || []);
    } catch { /* a failed folder load is not an empty mailbox; keep what we had */ }
  }, []);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  // Folders (2026-10-21): every account's own folders. A failed load is said, never "no folders".
  const fetchMailboxFolders = useCallback(async () => {
    setMailboxesError(null);
    try {
      const res = await api('/api/email/mailbox-folders');
      const data = await res.json();
      setMailboxes(Array.isArray(data.mailboxes) ? data.mailboxes : []);
    } catch (err) {
      setMailboxesError(messageFromError(err, 'Your mailbox folders could not be loaded.'));
    }
  }, []);
  useEffect(() => { if (connections.length > 0) fetchMailboxFolders(); }, [connections, fetchMailboxFolders]);

  // An Aidey folder's emails. A failed load is an error, not an empty folder.
  useEffect(() => {
    if (!activeFolderId) { setFolderAssignments(null); setFolderError(null); return undefined; }
    let live = true;
    setFolderError(null);
    setFolderAssignments(null);
    api(`/api/email/folders/${activeFolderId}/assignments`)
      .then((r) => r.json())
      .then((d) => { if (live) setFolderAssignments(d.assignments || []); })
      .catch((err) => { if (live) setFolderError(messageFromError(err, 'This folder could not be loaded.')); });
    return () => { live = false; };
  }, [activeFolderId, folderReload]);

  // ── Selection + mark read ──────────────────────────────────────────────────

  // 2026-10-22 (Carla): the list carries no bodies (it was every email's full HTML, and slow). An
  // email's body is loaded when it is opened: from the inbox the server just fetched
  // (GET /api/email/body/:id), else from the provider (GET /api/email/message/:id).
  const bodies = useRef(new Map());
  const withBody = useCallback(async (email) => {
    if (!email || email.body != null || email.provider === 'aidey') return email;
    const key = keyOf(email);
    if (bodies.current.has(key)) return { ...email, body: bodies.current.get(key) };
    let body;
    try {
      body = (await (await api(`/api/email/body/${encodeURIComponent(email.id)}`)).json()).body ?? '';
    } catch (err) {
      if (err?.status !== 404) throw err;
      const q = new URLSearchParams({ folder: activeFolderRef.current || 'inbox', ...(email.connectionId != null ? { connectionId: String(email.connectionId) } : {}) });
      body = (await (await api(`/api/email/message/${encodeURIComponent(email.id)}?${q}`)).json()).message?.body ?? '';
    }
    bodies.current.set(key, body);
    return { ...email, body };
  }, []);

  // Try again (2026-10-22): it said nothing. Now the button reads "Trying again…" while the inbox is
  // asked again (a failed mailbox is never served from the server's cache), and the banner goes on
  // success or says when it was tried and why.
  const [retrying, setRetrying] = useState(false);
  const [triedAgain, setTriedAgain] = useState(false);
  const retryMailboxes = useCallback(async () => {
    setRetrying(true);
    try { await fetchEmails(1, searchQueryRef.current); setTriedAgain(true); } finally { setRetrying(false); }
  }, [fetchEmails]);

  useEffect(() => { if (!failedConnections.length) setTriedAgain(false); }, [failedConnections]);

  const handleSelect = useCallback(async (email) => {
    setSelected(email);
    setShowReply(false);
    withBody(email)
      .then((full) => setSelected((prev) => (prev && keyOf(prev) === keyOf(email) ? { ...prev, body: full.body, bodyError: null } : prev)))
      .catch((err) => setSelected((prev) => (prev && keyOf(prev) === keyOf(email) ? { ...prev, bodyError: messageFromError(err, 'This email could not be opened. Try again.') } : prev)));
    if (!email.isRead && !imapOutsideInbox(email)) {
      try {
        await api(`/api/email/inbox/${email.id}/read`, {
          method: 'POST',
          body: JSON.stringify({ provider: email.provider, connectionId: email.connectionId }),
        });
        setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, isRead: true } : e)));
        setSelected((prev) => (prev?.id === email.id ? { ...prev, isRead: true } : prev));
      } catch (err) {
        // Block 233: expired Gmail scopes are the one case where the user is told exactly
        // what to do to fix it.
        if (err?.status === 403) {
          showToast('Gmail permissions need updating, please reconnect Gmail in Settings > Email connections', 'error');
        }
      }
    }
  }, [showToast, withBody]);

  const closeReading = useCallback(() => { setSelected(null); setShowReply(false); }, []);

  // ── Star ───────────────────────────────────────────────────────────────────

  const handleStar = useCallback(async (email) => {
    const newStarred = !email.isStarred;
    setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, isStarred: newStarred } : e)));
    setSelected((prev) => (prev?.id === email.id ? { ...prev, isStarred: newStarred } : prev));
    try {
      await api(`/api/email/inbox/${email.id}/star`, {
        method: 'POST',
        body: JSON.stringify({ provider: email.provider, connectionId: email.connectionId, starred: newStarred }),
      });
    } catch (err) {
      setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, isStarred: email.isStarred } : e)));
      setSelected((prev) => (prev?.id === email.id ? { ...prev, isStarred: email.isStarred } : prev));
      showToast(messageFromError(err, newStarred ? 'That email could not be starred.' : 'That email could not be unstarred.'), 'error');
    }
  }, [showToast]);

  // ── Send reply ─────────────────────────────────────────────────────────────

  const handleSend = useCallback(async ({ to, subject, body, email }) => {
    setReplying(true);
    try {
      await api('/api/email/reply', {
        method: 'POST',
        body: JSON.stringify({
          messageId: email.id,
          threadId: email.threadId,
          provider: email.provider,
          connectionId: email.connectionId || null,
          to,
          subject,
          body: body.replace(/\n/g, '<br>'),
        }),
      });
      setShowReply(false);
      showToast('Reply sent');
    } catch (err) {
      showToast(`Failed to send: ${messageFromError(err, 'the reply was not sent.')}`, 'error');
    } finally {
      setReplying(false);
    }
  }, [showToast]);

  // Compose a brand-new email (or a forward). Routes through the same /api/email/reply endpoint
  // as replies (messageId/threadId null = a fresh email); the From selector maps to a specific
  // connection via provider + connectionId, no forked send logic.
  const handleCompose = useCallback(async ({ connection, to, cc, bcc, subject, body }) => {
    setComposing(true);
    try {
      await api('/api/email/reply', {
        method: 'POST',
        body: JSON.stringify({
          messageId: null,
          threadId: null,
          provider: connection.provider,
          connectionId: connection.id,
          to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          subject,
          body: body.replace(/\n/g, '<br>'),
        }),
      });
      setCompose(null);
      showToast('Email sent');
      if (activeFolderRef.current === 'sent') fetchEmails(1, '', activeProviderRef.current, 'sent');
    } catch (err) {
      showToast(`Failed to send: ${messageFromError(err, 'the email was not sent.')}`, 'error');
    } finally {
      setComposing(false);
    }
  }, [showToast, fetchEmails]);

  const improveDraft = useCallback(async ({ subject, body, to }) => {
    try {
      const res = await api('/api/email/improve', { method: 'POST', body: JSON.stringify({ subject, body, to }) });
      return await res.json();
    } catch (err) {
      throw new Error(messageFromError(err, 'Could not improve the draft.'));
    }
  }, []);

  // ── Multi-select ──────────────────────────────────────────────────────────

  const handleToggleSelect = useCallback((emailKey) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(emailKey)) next.delete(emailKey); else next.add(emailKey);
      return next;
    });
  }, []);

  // ── Delete with undo ──────────────────────────────────────────────────────

  // Undo = restore-from-trash. The delete has ALREADY been committed on the provider;
  // undo re-inserts the emails locally and moves them back out of trash server-side.
  const handleUndo = useCallback(() => {
    clearTimeout(deleteTimeoutRef.current);
    pendingDeleteRef.current = new Set();
    const snapshot = pendingDeleteItems;
    const handles = restoreHandlesRef.current;
    setPendingDeleteItems(null);
    closeToast();
    if (snapshot) {
      setEmails((cur) => {
        const existing = new Set(cur.map(keyOf));
        const toAdd = snapshot.filter((e) => !existing.has(keyOf(e)));
        return [...toAdd, ...cur].sort((a, b) => new Date(b.date) - new Date(a.date));
      });
      if (handles && handles.length) {
        api('/api/email/inbox/restore', { method: 'POST', body: JSON.stringify({ items: handles }) })
          .catch((err) => showToast(`Could not restore: ${messageFromError(err, 'the email stays in Deleted.')}`, 'error'));
      }
    }
    restoreHandlesRef.current = [];
  }, [pendingDeleteItems, closeToast, showToast]);
  undoRef.current = handleUndo;   // the toast's Undo always runs the current one

  // Restore trashed mail back to the Inbox (from the Deleted view). Reuses the
  // same /inbox/restore endpoint as the delete-undo, with handles built from the
  // deleted email (imap needs trashPath+uid, which the /deleted list carries).
  const handleRestore = useCallback(async (emailsToRestore) => {
    if (!emailsToRestore.length) return;
    const handles = emailsToRestore.map((e) => (e.provider === 'imap'
      ? { originalId: e.id, provider: 'imap', connectionId: e.connectionId, trashPath: e.mailboxPath, uid: e.uid }
      : { originalId: e.id, provider: e.provider, connectionId: e.connectionId, id: e.id }));
    const keys = new Set(emailsToRestore.map(keyOf));
    setEmails((prev) => prev.filter((e) => !keys.has(keyOf(e))));
    setSelected((prev) => (prev && keys.has(keyOf(prev)) ? null : prev));
    try {
      const res = await api('/api/email/inbox/restore', { method: 'POST', body: JSON.stringify({ items: handles }) });
      const data = await res.json().catch(() => ({}));
      showToast(`Restored ${data.restored || emailsToRestore.length} to Inbox`);
    } catch (err) {
      showToast(`Failed to restore: ${messageFromError(err, 'nothing was restored.')}`, 'error');
      fetchEmails(1, '', activeProviderRef.current, 'deleted'); // resync on failure
    }
  }, [showToast, fetchEmails]);

  // Permanently empty ONE account's trash (the account currently in view).
  // Gmail is never offered (scope-limited); "All accounts" never empties everything.
  const emptyTrashBody = activeProvider === 'outlook' ? { provider: 'outlook', connectionId: activeConnectionId || undefined }
    : (activeProvider === 'imap' && activeConnectionId ? { provider: 'imap', connectionId: activeConnectionId } : null);
  const trashCount = pagination?.total ?? emails.length;
  const handleEmptyTrash = useCallback(async () => {
    if (!emptyTrashBody) return;
    try {
      const res = await api('/api/email/deleted/empty', { method: 'POST', body: JSON.stringify(emptyTrashBody) });
      const data = await res.json();
      const n = data.deleted ?? trashCount;
      showToast(`Permanently deleted ${plural(n, 'email')}`);
      setEmails([]);
      setSelected(null);
      fetchEmails(1, '', activeProviderRef.current, 'deleted');
    } catch (err) {
      // ConfirmDialog shows this inside the dialog.
      throw new Error(err?.body?.message || messageFromError(err, 'Trash could not be emptied.'));
    }
  }, [emptyTrashBody, trashCount, showToast, fetchEmails]);

  const handleDelete = useCallback(async (emailsToDelete) => {
    if (!emailsToDelete.length) return;
    // Clear any prior undo window (its delete was already committed).
    clearTimeout(deleteTimeoutRef.current);
    // Optimistic remove + client-side tombstone so a refresh mid-request can't re-add it.
    const keys = new Set(emailsToDelete.map(keyOf));
    pendingDeleteRef.current = keys;
    setEmails((prev) => prev.filter((e) => !keys.has(keyOf(e))));
    setSelected((prev) => (prev && keys.has(keyOf(prev)) ? null : prev));
    setSelectedIds(new Set());
    setPendingDeleteItems(emailsToDelete);
    const label = emailsToDelete.length === 1 ? 'Email' : `${emailsToDelete.length} emails`;
    showToast(`${label} deleted`, 'success', true);

    // Commit the provider delete IMMEDIATELY (no deferred timer).
    try {
      const res = await api('/api/email/inbox/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ items: emailsToDelete.map((e) => ({ id: e.id, provider: e.provider, connectionId: e.connectionId })) }),
      });
      const data = await res.json();
      restoreHandlesRef.current = Array.isArray(data.restore) ? data.restore : [];

      // Some items may have failed server-side even on HTTP 200: put exactly those back in
      // local state so they don't silently reappear on the next refresh, and say so.
      const failedList = Array.isArray(data.failed) ? data.failed : [];
      // No Trash folder is a question, not a failure: those come back to the list (below) and the
      // person is asked before anything is deleted for good.
      const askList = failedList.filter((f) => f.code === 'NO_TRASH_FOLDER');
      if (askList.length) {
        const askKeys = new Set(askList.map((f) => `${f.provider}-${f.id}`));
        setNoTrash({ emails: emailsToDelete.filter((e) => askKeys.has(keyOf(e))), question: askList[0].error });
        if (askList.length === emailsToDelete.length) { setPendingDeleteItems(null); closeToast(); }
      }
      if (failedList.length) {
        const failedKeys = new Set(failedList.map((f) => `${f.provider}-${f.id}`));
        const toRestore = emailsToDelete.filter((e) => failedKeys.has(keyOf(e)));
        pendingDeleteRef.current = new Set([...pendingDeleteRef.current].filter((k) => !failedKeys.has(k)));
        if (toRestore.length) {
          setEmails((prev) => {
            const existing = new Set(prev.map(keyOf));
            const add = toRestore.filter((e) => !existing.has(keyOf(e)));
            return [...add, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date));
          });
        }
        const realFailures = failedList.filter((f) => f.code !== 'NO_TRASH_FOLDER');
        const n = realFailures.length;
        // The server's real per-item reason; when items failed for DIFFERENT reasons, one
        // representative message and how many other distinct errors there were.
        const distinctErrors = [...new Set(realFailures.map((f) => f.error).filter(Boolean))];
        const detail = distinctErrors[0] || 'connection issue';
        const others = distinctErrors.length - 1;
        const more = others > 0 ? ` (+${others} other error${others === 1 ? '' : 's'})` : '';
        if (n) showToast(`Couldn't delete ${plural(n, 'email')}: ${detail}${more}`, 'error');
      }
    } catch (err) {
      // Commit failed: put them back and say why.
      restoreHandlesRef.current = [];
      pendingDeleteRef.current = new Set();
      setPendingDeleteItems(null);
      showToast(`Delete failed: ${messageFromError(err, 'nothing was deleted.')}`, 'error');
      setEmails((prev) => {
        const existing = new Set(prev.map(keyOf));
        const toRestore = emailsToDelete.filter((e) => !existing.has(keyOf(e)));
        return [...toRestore, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date));
      });
      return;
    }

    // Keep the undo window open for 8s, then just close it (delete already committed).
    deleteTimeoutRef.current = setTimeout(() => {
      pendingDeleteRef.current = new Set();
      restoreHandlesRef.current = [];
      setPendingDeleteItems(null);
      closeToast();
    }, 8000);
  }, [showToast, closeToast]);

  // ── Email Phase 1 (Carla, 2026-10-21): Mark as junk and Block sender ──────────────────────
  // Junk moves it in the MAILBOX (the server's answer carries the handle to move it back); the
  // toast's Undo is its own, so a delete's Undo is never confused with it.
  const byDate = (list) => list.sort((a, b) => new Date(b.date) - new Date(a.date));
  const handleJunk = useCallback(async (email) => {
    const key = keyOf(email);
    setEmails((prev) => prev.filter((e) => keyOf(e) !== key));
    setSelected((prev) => (prev && keyOf(prev) === key ? null : prev));
    try {
      const res = await api(`/api/email/inbox/${encodeURIComponent(email.id)}/junk`, {
        method: 'POST', body: JSON.stringify({ provider: email.provider, connectionId: email.connectionId }),
      });
      const data = await res.json();
      closeToast();
      toastElRef.current = showSharedToast('Moved to Junk', data.undo ? {
        type: 'success', ms: 8000,
        action: { label: 'Undo', onClick: async () => {
          closeToast();
          try {
            await api('/api/email/inbox/not-junk', { method: 'POST', body: JSON.stringify({ undo: data.undo }) });
            setEmails((prev) => byDate([email, ...prev.filter((e) => keyOf(e) !== key)]));
            showToast('Moved back to the Inbox');
          } catch (err) {
            showToast(`Not moved back: ${messageFromError(err, 'it is still in Junk.')}`, 'error');
          }
        } },
      } : { type: 'success' });
    } catch (err) {
      setEmails((prev) => byDate([email, ...prev.filter((e) => keyOf(e) !== key)]));
      showToast(messageFromError(err, 'That email was not moved to Junk. Nothing was changed.'), 'error');
    }
  }, [closeToast, showToast]);

  // Block sender: the confirm says what happens; a refusal shows inside it (lib/blockedSenders).
  const handleBlock = useCallback(async () => {
    const email = blockFor;
    if (!email || !email.fromEmail) return;
    try {
      await api('/api/email/blocked-senders', { method: 'POST', body: JSON.stringify({ address: email.fromEmail }) });
    } catch (err) {
      throw new Error(messageFromError(err, 'That sender was not blocked.'));
    }
    const addr = String(email.fromEmail).toLowerCase();
    setEmails((prev) => prev.filter((e) => String(e.fromEmail || '').toLowerCase() !== addr));
    setSelected((prev) => (prev && String(prev.fromEmail || '').toLowerCase() === addr ? null : prev));
    showToast(`Blocked ${email.fromEmail}. Their emails will go to Junk.`);
  }, [blockFor, showToast]);

  // ── Email Phase 2 (Carla, 2026-10-21): Snooze, Remind if no reply, Clean up ─────────────────────
  // Snooze: out of the inbox (archived in the mailbox) until the time; Undo brings it back now.
  const snoozeChoices = () => {
    const now = new Date();
    const at = (d, h) => { const x = new Date(d); x.setHours(h, 0, 0, 0); return x; };
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    const monday = new Date(now); monday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
    return [
      ...(now.getHours() < 17 ? [{ id: 'later', label: 'Later today (6pm)', until: at(now, 18) }] : []),
      { id: 'tomorrow', label: 'Tomorrow morning (9am)', until: at(tomorrow, 9) },
      { id: 'week', label: 'Next week (Monday 9am)', until: at(monday, 9) },
    ];
  };
  const handleSnooze = useCallback(async (email, until) => {
    const key = keyOf(email);
    setEmails((prev) => prev.filter((e) => keyOf(e) !== key));
    setSelected((prev) => (prev && keyOf(prev) === key ? null : prev));
    try {
      const d = await (await api(`/api/email/inbox/${encodeURIComponent(email.id)}/snooze`, {
        method: 'POST', body: JSON.stringify({ provider: email.provider, connectionId: email.connectionId, until: until.toISOString() }),
      })).json();
      if (d.snoozed === false) { showToast(d.note || 'It was archived.'); return; }
      closeToast();
      toastElRef.current = showSharedToast(`Snoozed until ${formatDateTime(until.toISOString())}`, {
        type: 'success', ms: 8000,
        action: { label: 'Undo', onClick: async () => {
          closeToast();
          try { await api(`/api/email/snoozes/${d.id}`, { method: 'DELETE' }); setEmails((prev) => byDate([email, ...prev.filter((e) => keyOf(e) !== key)])); showToast('Back in the Inbox'); }
          catch (err) { showToast(`Not moved back: ${messageFromError(err, 'it is still snoozed.')}`, 'error'); }
        } },
      });
    } catch (err) {
      setEmails((prev) => byDate([email, ...prev.filter((e) => keyOf(e) !== key)]));
      showToast(messageFromError(err, 'That email was not snoozed.'), 'error');
    }
  }, [closeToast, showToast]);

  const handleRemind = useCallback(async (email, days) => {
    try {
      const d = await (await api('/api/email/reply-reminders', { method: 'POST', body: JSON.stringify({ emailId: email.id, days }) })).json();
      showToast(`I'll remind you if ${d.reminder?.toEmail || 'they'} hasn't replied in ${days} days.`);
    } catch (err) { showToast(messageFromError(err, 'That reminder was not set.'), 'error'); }
  }, [showToast]);

  const openCleanup = useCallback(async () => {
    setCleanup({ status: 'loading', list: [], error: null });
    try {
      const d = await (await api('/api/email/cleanup')).json();
      setCleanup({ status: 'ready', list: d.suggestions || [], note: d.note || null, error: null });
    } catch (err) { setCleanup({ status: 'failed', list: [], error: messageFromError(err, 'The clean-up could not look through your inbox.') }); }
  }, []);
  const archiveSender = useCallback(async (sender) => {
    try {
      const d = await (await api('/api/email/cleanup/archive-sender', { method: 'POST', body: JSON.stringify({ sender }) })).json();
      setEmails((prev) => prev.filter((e) => String(e.fromEmail || '').toLowerCase() !== sender));
      setCleanup((c) => (c ? { ...c, list: c.list.filter((g) => g.sender !== sender) } : c));
      showToast(`Archived ${d.archived} ${d.archived === 1 ? 'email' : 'emails'} from ${sender}.`);
    } catch (err) { showToast(messageFromError(err, 'Nothing was archived.'), 'error'); }
  }, [showToast]);

  // "Delete anyway": permanent, so no Undo. A failure shows inside the dialog.
  const handleDeleteForGood = useCallback(async () => {
    const toDelete = noTrash?.emails || [];
    if (!toDelete.length) return;
    const res = await api('/api/email/inbox/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ items: toDelete.map((e) => ({ id: e.id, provider: e.provider, connectionId: e.connectionId, permanent: true })) }),
    }).catch((err) => { throw new Error(messageFromError(err, 'Nothing was deleted.')); });
    const data = await res.json();
    const failedIds = new Set((Array.isArray(data.failed) ? data.failed : []).map((f) => `${f.provider}-${f.id}`));
    const gone = new Set(toDelete.map(keyOf).filter((k) => !failedIds.has(k)));
    setEmails((prev) => prev.filter((e) => !gone.has(keyOf(e))));
    setSelected((prev) => (prev && gone.has(keyOf(prev)) ? null : prev));
    if (failedIds.size) throw new Error(`${plural(failedIds.size, 'email')} could not be deleted: ${data.failed[0].error || 'connection issue'}`);
    showToast(`${gone.size === 1 ? 'Email' : plural(gone.size, 'email')} deleted for good`);
  }, [noTrash, showToast]);

  // ── Mark read/unread (bulk, one email, or everything shown) ─────────────────

  const markRead = useCallback(async (toMark, markAsRead) => {
    if (!toMark.length) return;
    const keys = new Set(toMark.map(keyOf));
    setEmails((prev) => prev.map((e) => (keys.has(keyOf(e)) ? { ...e, isRead: markAsRead } : e)));
    try {
      await Promise.all(toMark.map((e) => api(`/api/email/inbox/${e.id}/read`, {
        method: 'POST',
        body: JSON.stringify({ provider: e.provider, connectionId: e.connectionId, isRead: markAsRead }),
      })));
    } catch (err) {
      setEmails((prev) => prev.map((e) => {
        const orig = toMark.find((m) => m.id === e.id && m.provider === e.provider);
        return orig ? { ...e, isRead: orig.isRead } : e;
      }));
      showToast(`Mark failed: ${messageFromError(err, 'nothing was changed.')}`, 'error');
    }
  }, [showToast]);

  const handleBulkMarkRead = useCallback((markAsRead) => {
    const toMark = emails.filter((e) => selectedIds.has(keyOf(e)));
    setSelectedIds(new Set());
    markRead(toMark, markAsRead);
  }, [emails, selectedIds, markRead]);

  const handleMarkUnread = useCallback((email) => {
    closeReading();
    markRead([{ ...email, isRead: true }], false);
  }, [closeReading, markRead]);

  // ── Move to folder ────────────────────────────────────────────────────────

  const handleMoveToFolder = useCallback(async (folderId) => {
    const toMove = emails.filter((e) => selectedIds.has(keyOf(e)));
    if (!toMove.length) return;
    try {
      await Promise.all(toMove.map((e) => api(`/api/email/folders/${folderId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ emailId: e.id, emailSource: e.provider }),
      })));
      showToast(`${plural(toMove.length, 'email')} moved to folder`);
      setSelectedIds(new Set());
      if (activeFolderId === folderId) setFolderReload((n) => n + 1);
    } catch (err) {
      showToast(`Move failed: ${messageFromError(err, 'nothing was moved.')}`, 'error');
    }
  }, [emails, selectedIds, activeFolderId, showToast]);

  // ── Folder CRUD ───────────────────────────────────────────────────────────

  const handleCreateFolder = useCallback(async (name) => {
    try {
      const res = await api('/api/email/folders', { method: 'POST', body: JSON.stringify({ name }) });
      const data = await res.json();
      if (data.folder) setFolders((prev) => [...prev, data.folder].sort((a, b) => a.name.localeCompare(b.name)));
      return true;
    } catch (err) {
      showToast(`Failed to create folder: ${messageFromError(err, 'the folder was not created.')}`, 'error');
      return false;
    }
  }, [showToast]);

  // Decision 7: the folder goes at once, the DELETE waits 6 seconds behind Undo.
  const folderDelete = useDelayedDelete({
    send: (f, { keepalive }) => api(`/api/email/folders/${f.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (f) => setFolders((prev) => prev.filter((x) => x.id !== f.id)),
    describe: (f) => `Folder "${f.name}" deleted.`,
    failMessage: 'That folder could not be deleted.',
  });
  const handleDeleteFolder = useCallback((f) => {
    if (activeFolderId === f.id) { setActiveFolderId(null); setFolderAssignments(null); }
    folderDelete.remove(f);
  }, [activeFolderId, folderDelete]);
  const visibleFolders = folders.filter((f) => !folderDelete.isHidden(f.id));

  // ── Mailbox folders (2026-10-21): create, rename, delete, move ─────────────
  const patchMailbox = (connectionId, fn) => setMailboxes((prev) => prev.map((m) => (m.connectionId === connectionId && Array.isArray(m.folders)
    ? { ...m, folders: fn(m.folders).slice().sort((a, b) => a.name.localeCompare(b.name)) } : m)));

  const handleCreateMailboxFolder = useCallback(async (mailbox, name) => {
    try {
      const res = await api('/api/email/mailbox-folders', { method: 'POST', body: JSON.stringify({ connectionId: mailbox.connectionId, name }) });
      const data = await res.json();
      if (data.folder) patchMailbox(mailbox.connectionId, (list) => [...list, data.folder]);
      showToast(`Folder "${data.folder?.name || name}" created`);
      return true;
    } catch (err) {
      showToast(messageFromError(err, 'That folder was not created.'), 'error');
      return false;
    }
  }, [showToast]);

  const handleRenameMailboxFolder = useCallback(async (mailbox, folder, name) => {
    try {
      const res = await api('/api/email/mailbox-folders/rename', { method: 'POST', body: JSON.stringify({ connectionId: mailbox.connectionId, folderId: folder.id, name }) });
      const data = await res.json();
      const next = data.folder || { id: folder.id, name };
      patchMailbox(mailbox.connectionId, (list) => list.map((f) => (f.id === folder.id ? { ...f, ...next } : f)));
      const open = activeMailboxFolderRef.current;
      if (open && open.connectionId === mailbox.connectionId && open.id === folder.id) {
        activeMailboxFolderRef.current = { ...open, id: next.id, name: next.name };
        setActiveMailboxFolder(activeMailboxFolderRef.current);
      }
      return true;
    } catch (err) {
      showToast(messageFromError(err, 'That folder was not renamed.'), 'error');
      return false;
    }
  }, [showToast]);

  // The confirm is ConfirmDialog's: a failure shows inside it, and the folder stays.
  const handleDeleteMailboxFolder = useCallback(async () => {
    const { mailbox, folder } = deletingFolder || {};
    if (!folder) return;
    let data;
    try {
      const res = await api('/api/email/mailbox-folders/delete', { method: 'POST', body: JSON.stringify({ connectionId: mailbox.connectionId, folderId: folder.id, confirmed: true }) });
      data = await res.json();
    } catch (err) {
      throw new Error(messageFromError(err, 'That folder was not deleted. Nothing was changed.'));
    }
    patchMailbox(mailbox.connectionId, (list) => list.filter((f) => f.id !== folder.id));
    const open = activeMailboxFolderRef.current;
    if (open && open.connectionId === mailbox.connectionId && open.id === folder.id) {
      activeMailboxFolderRef.current = null;
      setActiveMailboxFolder(null);
      setActiveFolder('inbox');
      activeFolderRef.current = 'inbox';
      setSelected(null);
      fetchEmails(1, '', activeProviderRef.current, 'inbox');
    }
    const n = data && data.emailsDeleted;
    showToast(data && data.emailsKept ? `Label "${folder.name}" deleted. Its emails are still in your mailbox.`
      : `Folder "${folder.name}" deleted${n ? ` with ${plural(n, 'email')}` : ''}`);
  }, [deletingFolder, showToast, fetchEmails]);

  // Out of the inbox IN the mailbox (Archive, or one of its folders), one email or many: ONE toast,
  // and ONE Undo that moves each back (POST /inbox/move-back takes an archive's handle and a
  // folder's). An email that did not move comes back to the list, and the reason is said.
  const moveOut = useCallback(async (list, { send, one, many, stillIn, failed: failedWords }) => {
    const keys = new Set(list.map(keyOf));
    setEmails((prev) => prev.filter((e) => !keys.has(keyOf(e))));
    setSelected((prev) => (prev && keys.has(keyOf(prev)) ? null : prev));
    const putBack = (back) => setEmails((prev) => {
      const k = new Set(back.map(keyOf));
      return byDate([...back, ...prev.filter((e) => !k.has(keyOf(e)))]);
    });
    const results = await Promise.allSettled(list.map(async (e) => (await send(e)).json()));
    const moved = []; const notMoved = []; let reason = null;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') moved.push({ email: list[i], undo: r.value && r.value.undo });
      else { notMoved.push(list[i]); if (!reason) reason = r.reason; }
    });
    if (notMoved.length) putBack(notMoved);
    closeToast();
    if (!moved.length) { showToast(messageFromError(reason, failedWords), 'error'); return; }
    const said = list.length === 1 ? one : many(moved.length);
    const msg = notMoved.length
      ? `${said}. ${notMoved.length} ${notMoved.length === 1 ? 'was' : 'were'} not: ${messageFromError(reason, 'it is still in your inbox.')}`
      : said;
    const undoable = moved.filter((m) => m.undo);
    toastElRef.current = showSharedToast(msg, undoable.length ? {
      type: 'success', ms: 8000,
      action: { label: 'Undo', onClick: async () => {
        closeToast();
        const back = await Promise.allSettled(undoable.map((m) => api('/api/email/inbox/move-back', { method: 'POST', body: JSON.stringify({ undo: m.undo }) })));
        const returned = undoable.filter((_, i) => back[i].status === 'fulfilled').map((m) => m.email);
        if (returned.length) putBack(returned);
        const miss = back.find((x) => x.status === 'rejected');
        if (miss) showToast(`Not moved back: ${messageFromError(miss.reason, stillIn)}`, 'error');
        else showToast('Moved back');
      } },
    } : { type: 'success' });
  }, [closeToast, showToast]);

  const archiveEmails = useCallback((list) => moveOut(list, {
    send: (e) => api('/api/email/archive', { method: 'POST', body: JSON.stringify({ emailId: e.id, provider: e.provider, connectionId: e.connectionId }) }),
    one: 'Archived', many: (n) => `${plural(n, 'email')} archived`,
    stillIn: 'it is still in Archive.', failed: 'That email was not archived. Nothing was changed.',
  }), [moveOut]);

  const moveToMailboxFolder = useCallback((list, folder) => moveOut(list, {
    send: (e) => api(`/api/email/inbox/${encodeURIComponent(e.id)}/move-to-folder`, {
      method: 'POST', body: JSON.stringify({ provider: e.provider, connectionId: e.connectionId, folderId: folder.id, ...moveSource(e) }),
    }),
    one: `Moved to ${folder.name}`, many: (n) => `${plural(n, 'email')} moved to ${folder.name}`,
    stillIn: `it is still in ${folder.name}.`, failed: 'That email was not moved. Nothing was changed.',
  }), [moveOut]);

  // Move to…, from the reading pane: Inbox or Archive, the email's own mailbox's folders, or an Aidey folder.
  const handleMoveEmail = useCallback(async (email, dest) => {
    if (dest.kind === 'archive') { archiveEmails([email]); return; }
    if (dest.kind === 'inbox') { moveToMailboxFolder([email], inboxFolderOf(email.provider)); return; }
    if (dest.kind === 'aidey') {
      try {
        await api(`/api/email/folders/${dest.folder.id}/assign`, { method: 'POST', body: JSON.stringify({ emailId: email.id, emailSource: email.provider }) });
        showToast(`Filed in ${dest.folder.name}`);
        if (activeFolderId === dest.folder.id) setFolderReload((n) => n + 1);
      } catch (err) {
        showToast(messageFromError(err, 'That email was not filed. Nothing was changed.'), 'error');
      }
      return;
    }
    moveToMailboxFolder([email], dest.folder);
  }, [activeFolderId, archiveEmails, moveToMailboxFolder, showToast]);

  // Move to…, from the bulk bar: the same destinations, for every selected email.
  const handleBulkMove = useCallback((dest) => {
    if (dest.kind === 'aidey') { handleMoveToFolder(dest.folder.id); return; }
    const list = emails.filter((e) => selectedIds.has(keyOf(e)));
    setSelectedIds(new Set());
    if (dest.kind === 'archive') archiveEmails(list);
    else if (dest.kind === 'inbox') moveToMailboxFolder(list, inboxFolderOf(list[0]?.provider));
    else moveToMailboxFolder(list, dest.folder);
  }, [emails, selectedIds, handleMoveToFolder, archiveEmails, moveToMailboxFolder]);

  // ── Inline draft suggestion ───────────────────────────────────────────────

  const handleDismissDraft = useCallback((emailId) => {
    setDismissedDrafts((prev) => new Set([...prev, emailId]));
  }, []);

  // ── Ask Aidey ─────────────────────────────────────────────────────────────

  const handleAskAidey = useCallback((email) => {
    const bodyText = stripHtml(email.body || '').slice(0, 200).trim();
    const context = `Help me with this email from ${email.fromName || email.fromEmail}: "${email.subject}".${bodyText ? `\n\n${bodyText}${bodyText.length === 200 ? '…' : ''}` : ''}`;
    if (typeof onAskAidey === 'function') {
      onAskAidey(context);
    } else {
      window.dispatchEvent(new CustomEvent('aidey:openChatWithContext', { detail: { context } }));
    }
  }, [onAskAidey]);

  // ── The Aidey summary card's actions (decision 5) ─────────────────────────
  // Each one an existing, permission-checked path. A failure throws with the server's words (the
  // card shows it inline, at the button); success is only ever said from an answer we have read.
  const handleSummaryAction = useCallback(async (action, listEmail) => {
    // These read the email's words (a task from it, a reply, its text), so its body first.
    let email = listEmail;
    if (action !== 'add_to_calendar') {
      try { email = await withBody(listEmail); } catch (err) { throw new Error(messageFromError(err, 'This email could not be opened. Try again.')); }
    }
    if (action === 'add_to_calendar') {
      let proposals;
      try {
        const res = await api('/api/event-proposals');
        proposals = await res.json();
      } catch (err) {
        throw new Error(messageFromError(err, 'Aidey could not check this email for a date.'));
      }
      const proposal = (Array.isArray(proposals) ? proposals : []).find((p) => p.source_email_reference === email.id);
      if (!proposal) return { message: 'Aidey has not found a date in this email that it can add.' };
      let data;
      try {
        const res = await api(`/api/event-proposals/${encodeURIComponent(proposal.id)}/accept`, { method: 'POST' });
        data = await res.json();
      } catch (err) {
        throw new Error(messageFromError(err, 'This could not be added to your calendar.'));
      }
      if (data?.requiresResolution) return { message: 'This clashes with something already in your calendar. Choose what to do in Notifications.', clash: true };
      if (data?.created) return { message: `Added to your calendar: ${data.title || proposal.title || 'the event'}.` };
      throw new Error('Aidey could not confirm this was added to your calendar.');
    }
    if (action === 'save_to_notes') {
      const text = [`From: ${email.from || email.fromEmail || ''}`, `Date: ${formatDateTime(email.date)}`, '', htmlToText(email.body)].join('\n');
      let data;
      try {
        const res = await api('/api/notes', { method: 'POST', body: JSON.stringify({ id: String(Date.now()), title: email.subject || '(no subject)', text, blocks: [] }) });
        data = await res.json();
      } catch (err) {
        throw new Error(messageFromError(err, 'This email could not be saved to Notes.'));
      }
      if (!data?.note?.id) throw new Error('Aidey could not confirm the note was saved.');
      window.dispatchEvent(new Event('aidey:notesUpdated'));
      return { message: 'Saved to Notes.' };
    }
    throw new Error('That action is not available.');
  }, [withBody]);

  const goTo = useCallback((view) => { if (typeof setFilter === 'function') setFilter(view); }, [setFilter]);

  // ── Write with Aidey ──────────────────────────────────────────────────────

  const handleWriteWithAidey = useCallback(({ email }) => {
    const prompt = `Draft a reply to this email from ${email.fromName || email.fromEmail}.\n\nOriginal subject: ${email.subject}\nOriginal message: ${stripHtml(email.body).slice(0, 400)}`;
    window.dispatchEvent(new CustomEvent('aidey:openChatWithContext', { detail: { context: prompt } }));
    if (typeof setFilter === 'function') setFilter('AideyChat');
  }, [setFilter]);

  // ── Forward: a new email from the same account, the original quoted ─────────

  const handleForward = useCallback((email) => {
    const conn = connections.find((c) => c.id === email.connectionId)
      || connections.find((c) => c.provider === email.provider);
    const subject = /^fwd?:/i.test(email.subject || '') ? email.subject : `Fwd: ${email.subject || ''}`;
    const original = email.provider === 'aidey' ? (email.draftBody || '') : htmlToText(email.body);
    const header = [
      '---------- Forwarded message ----------',
      `From: ${email.from || senderName(email)}`,
      email.date ? `Date: ${formatDateTime(email.date)}` : null,
      `Subject: ${email.subject || ''}`,
      email.to ? `To: ${email.to}` : null,
    ].filter(Boolean).join('\n');
    setCompose({
      title: 'Forward', subject, body: `\n\n${header}\n\n${original}`, fromId: conn?.id ?? null,
      note: 'The message text is forwarded. Attachments are not included.',
    });
  }, [connections]);

  // ── Sender automation: "done" only once the server has agreed ──────────────

  const handleSenderAutomation = useCallback(async (email, intentType, on) => {
    try {
      await api('/api/email/automation/senders', {
        method: 'POST',
        body: JSON.stringify({ senderEmail: email.fromEmail, senderName: email.fromName, intentType, enabled: on, ...(on ? { autoSend: true } : {}) }),
      });
      showToast(on ? 'Always auto-send for this sender' : 'Automation blocked for this sender');
    } catch (err) {
      showToast(messageFromError(err, 'That automation setting was not saved.'), 'error');
    }
  }, [showToast]);

  // ── Add to CRM ────────────────────────────────────────────────────────────

  const handleAddToCrm = useCallback(async (email) => {
    try {
      await api('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({ name: email.fromName || email.fromEmail, email: email.fromEmail }),
      });
      showToast(`${email.fromName || email.fromEmail} added to CRM`);
      window.dispatchEvent(new Event('aidey:contactsUpdated'));
    } catch (err) {
      showToast(`Failed to add to CRM: ${messageFromError(err, 'the contact was not added.')}`, 'error');
    }
  }, [showToast]);

  // ── Spam ──────────────────────────────────────────────────────────────────

  const handleNotSpam = useCallback(async (email) => {
    try {
      // The sender goes with it: Not spam trusts them from then on (2026-10-22). notSpam (2026-10-23):
      // the email is in the mailbox's own Junk, and the server moves it back to the Inbox there first.
      await api(`/api/email/spam/${email.id}/restore`, { method: 'POST', body: JSON.stringify({ fromEmail: email.fromEmail, ...(email.notSpam ? { notSpam: email.notSpam } : {}) }) });
      setSpamEmails((prev) => prev.filter((e) => e.id !== email.id));
      setSpamCount((prev) => Math.max(0, prev - (email.spamActioned ? 0 : 1)));
      if (email.notSpam) showToast('Moved to your Inbox. Mail from this sender will not go to Spam again.', 'success');
    } catch (err) { showToast(messageFromError(err, 'Could not restore that email.'), 'error'); }
  }, [showToast]);

  const handleConfirmSpam = useCallback(async (email) => {
    try {
      await api(`/api/email/spam/${email.id}/confirm`, { method: 'POST', body: JSON.stringify({ provider: email.provider, connectionId: email.connectionId }) });
      setSpamEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, spamActioned: true } : e)));
      setSpamCount((prev) => Math.max(0, prev - 1));
    } catch (err) { showToast(messageFromError(err, 'Could not confirm that as spam.'), 'error'); }
  }, [showToast]);

  // Possible spam, left in the inbox (2026-10-22): Not spam clears the label and trusts the sender;
  // Confirm spam moves it to Junk in the mailbox and blocks the sender (both on the server).
  const handlePossibleNotSpam = useCallback(async (email) => {
    try {
      await api(`/api/email/spam/${email.id}/restore`, { method: 'POST', body: JSON.stringify({ fromEmail: email.fromEmail }) });
      const clear = (e) => (keyOf(e) === keyOf(email) ? { ...e, possibleSpam: false, spamReason: null } : e);
      setEmails((prev) => prev.map(clear));
      setSelected((prev) => (prev ? clear(prev) : prev));
    } catch (err) { showToast(messageFromError(err, 'Could not mark that as Not spam.'), 'error'); }
  }, [showToast]);
  const handlePossibleConfirm = useCallback(async (email) => {
    try {
      await api(`/api/email/spam/${email.id}/confirm`, { method: 'POST', body: JSON.stringify({ provider: email.provider, connectionId: email.connectionId, fromEmail: email.fromEmail }) });
      setEmails((prev) => prev.filter((e) => keyOf(e) !== keyOf(email)));
      setSelected((prev) => (prev && keyOf(prev) === keyOf(email) ? null : prev));
      showToast('Moved to Spam, and the sender is blocked.', 'success');
    } catch (err) { showToast(messageFromError(err, 'Could not confirm that as spam.'), 'error'); }
  }, [showToast]);

  // ── Search input handler ───────────────────────────────────────────────────

  function handleSearchChange(val) {
    setSearchInput(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const q = val.trim().toLowerCase();
      setSearchQuery(q);
      fetchEmails(1, q, activeProvider);
      if (emailListRef.current) emailListRef.current.scrollTop = 0;
    }, 300);
  }

  function clearSearch() {
    setSearchInput('');
    setSearchQuery('');
    clearTimeout(searchDebounceRef.current);
    fetchEmails(1, '', activeProvider);
    if (emailListRef.current) emailListRef.current.scrollTop = 0;
  }

  const goPage = (page) => {
    fetchEmails(page, searchQuery, activeProvider);
    if (emailListRef.current) emailListRef.current.scrollTop = 0;
  };
  const retry = () => fetchEmails(currentPage, searchQuery, activeProvider);
  const refresh = () => {
    if (activeFolder === 'spam') { fetchSpamEmails(); return; }
    if (activeFolder === 'drafts') fetchAideyDrafts();
    if (activeFolderId) setFolderReload((n) => n + 1);
    fetchEmails(1, searchQuery, activeProvider);
  };

  // ── Folder and account switching ───────────────────────────────────────────

  const leaveMailboxFolder = () => { activeMailboxFolderRef.current = null; setActiveMailboxFolder(null); };
  const openFolder = (key) => {
    leaveMailboxFolder();
    setActiveFolder(key);
    setTriageFilter(null);
    setActiveFolderId(null);
    setReadFilter('all');
    setSelected(null);
    setSelectedIds(new Set());
    setShowFolders(false);
    if (key === 'sent' || key === 'deleted') fetchEmails(1, '', activeProvider, key);
    else if (key === 'drafts') { fetchEmails(1, '', activeProvider, 'drafts'); fetchAideyDrafts(); }
    else if (key === 'spam') fetchSpamEmails();
    else if (activeFolderRef.current !== 'inbox' && activeFolderRef.current !== 'starred') fetchEmails(1, searchQuery, activeProvider, 'inbox');
  };
  const openAccount = (conn) => {
    const provider = conn ? conn.provider : null;
    const connId = conn && conn.provider === 'imap' ? conn.id : null;
    setActiveProvider(provider);
    setActiveConnectionId(connId);
    setActiveFolderId(null);
    setShowFolders(false);
    // An account is chosen for the Inbox (and the folders like it); a mailbox folder is one account's.
    const fromMailbox = activeFolderRef.current === 'mailbox';
    if (fromMailbox) { leaveMailboxFolder(); setActiveFolder('inbox'); activeFolderRef.current = 'inbox'; }
    fetchEmails(1, searchQuery, provider, fromMailbox ? 'inbox' : null, connId);
  };
  const openMailboxFolder = (mailbox, f) => {
    activeMailboxFolderRef.current = { connectionId: mailbox.connectionId, provider: mailbox.provider, id: f.id, name: f.name };
    setActiveMailboxFolder(activeMailboxFolderRef.current);
    setActiveFolder('mailbox');
    activeFolderRef.current = 'mailbox';
    setActiveFolderId(null);
    setTriageFilter(null);
    setReadFilter('all');
    setSelected(null);
    setSelectedIds(new Set());
    setShowFolders(false);
    setEmails([]);
    fetchEmails(1, '', null, 'mailbox');
  };
  const openAideyFolder = (f) => {
    // An Aidey folder files emails from the Inbox list, so the Inbox list is what it filters.
    if (!['inbox', 'starred'].includes(activeFolderRef.current)) {
      leaveMailboxFolder();
      activeFolderRef.current = 'inbox';
      fetchEmails(1, '', activeProvider, 'inbox');
    }
    setActiveFolderId(f.id);
    setActiveFolder('inbox');
    setTriageFilter(null);
    setSelected(null);
    setShowFolders(false);
  };

  // ── Derived email list ─────────────────────────────────────────────────────

  let displayEmails = emails;
  if (activeFolder === 'starred') displayEmails = displayEmails.filter((e) => e.isStarred);
  if (triageFilter) displayEmails = displayEmails.filter((e) => e.triageCategory === triageFilter);
  if (activeFolderId && folderAssignments) {
    const assignedIds = new Set(folderAssignments.map((a) => a.emailId));
    displayEmails = displayEmails.filter((e) => assignedIds.has(e.id));
  }
  if (readFilter === 'unread') displayEmails = displayEmails.filter((e) => !e.isRead);
  if (readFilter === 'starred') displayEmails = displayEmails.filter((e) => e.isStarred);

  const unreadShown = displayEmails.filter((e) => !e.isRead);
  const allSelected = displayEmails.length > 0 && displayEmails.every((e) => selectedIds.has(keyOf(e)));
  const selectedEmailsList = emails.filter((e) => selectedIds.has(keyOf(e)));
  const activeConnections = connections.filter((c) => MAIL_PROVIDERS.includes(c.provider));
  const accountKey = activeConnectionId ? `imap-${activeConnectionId}` : (activeProvider || 'all');
  const metaFor = (e) => accountMeta(e, activeConnections);
  const onePage = !pagination || (pagination.pages || 1) <= 1;

  const header = (subline) => (
    <PageHeader
      eyebrow={{ label: isPersonal ? 'My stuff' : 'My work', color: 'group-my-work' }}
      title="Emails"
      subline={subline}
      primary={activeConnections.length > 0 ? <HeaderButton primary onClick={() => setCompose({})}>+ New email</HeaderButton> : undefined}
      secondary={activeConnections.length > 0 ? [{ label: 'Refresh', onClick: refresh }, { label: 'Clean up', onClick: openCleanup }] : []}
    />
  );

  // ── Before the mailbox: checking, failed, nothing connected ────────────────

  if (!connectionChecked) {
    return (
      <PageLayout data-page="emails">
        {header('Checking your email accounts…')}
        <div className="em-shell em-shell--state"><Skeleton lines={6} label="Loading your email accounts" /></div>
      </PageLayout>
    );
  }

  if (connectionsError) {
    return (
      <PageLayout data-page="emails">
        {header('Your connected mailboxes')}
        <ErrorState title="Your email accounts did not load" message={connectionsError}
          onRetry={() => { setConnectionChecked(false); fetchConnections(); }} />
      </PageLayout>
    );
  }

  if (activeConnections.length === 0) {
    return (
      <PageLayout data-page="emails">
        {header('Read and answer your email here.')}
        <EmptyState icon="✉️" title="Connect your email"
          action={<Button variant="create" onClick={() => gotoSettings('email')}>Connect email account</Button>}>
          Connect Gmail, Outlook or another email provider to see and answer your inbox in Aidey.
        </EmptyState>
      </PageLayout>
    );
  }

  const accountName = (() => {
    if (accountKey === 'all') return null;
    const c = activeConnections.find((x) => (x.provider === 'imap' ? `imap-${x.id}` : x.provider) === accountKey);
    return c ? connectionLabel(c) : null;
  })();
  // Fresh-eyes item 13: the header says exactly what it counts, the Inbox of the chosen account(s),
  // and the Inbox folder shows the same number (they said 128 and 9).
  const inboxNow = inboxCounts && inboxCounts.account === accountKey ? inboxCounts : null;
  const subline = inboxNow
    ? `${inboxNow.unread} unread in Inbox · ${plural(inboxNow.total, 'email')} · ${accountName || plural(activeConnections.length, 'account')}`
    : `${plural(activeConnections.length, 'account')} connected`;

  // ── Folder navigation (a pane, or a sheet) ─────────────────────────────────

  const systemFolders = [
    { key: 'inbox', label: 'Inbox', count: inboxNow ? inboxNow.unread : undefined },
    { key: 'starred', label: 'Starred' },
    { key: 'sent', label: 'Sent' },
    { key: 'drafts', label: 'Drafts', count: aideyDrafts.length },
    { key: 'spam', label: 'Spam', count: spamCount },
    { key: 'deleted', label: 'Deleted' },
  ].map((f) => ({ ...f, active: activeFolder === f.key && !activeFolderId, onClick: () => openFolder(f.key) }));
  const nav = (
    <FolderNav systemFolders={systemFolders} connections={activeConnections} accountKey={accountKey} onAccount={openAccount}
      aideyFolders={visibleFolders} activeAideyFolderId={activeFolderId} onAideyFolder={openAideyFolder}
      onDeleteAideyFolder={handleDeleteFolder} onCreateFolder={handleCreateFolder}
      mailboxes={mailboxes} mailboxesError={mailboxesError} onRetryMailboxes={fetchMailboxFolders}
      activeMailboxFolderKey={activeFolder === 'mailbox' && activeMailboxFolder ? mailboxFolderKey(activeMailboxFolder.connectionId, activeMailboxFolder.id) : null}
      onMailboxFolder={openMailboxFolder} onCreateMailboxFolder={handleCreateMailboxFolder}
      onRenameMailboxFolder={handleRenameMailboxFolder} onDeleteMailboxFolder={(mailbox, folder) => setDeletingFolder({ mailbox, folder })} />
  );
  const activeAideyFolder = folders.find((f) => f.id === activeFolderId);
  const viewName = activeAideyFolder ? activeAideyFolder.name
    : (activeFolder === 'mailbox' && activeMailboxFolder ? activeMailboxFolder.name : FOLDER_LABEL[activeFolder]);
  // Move to…: Archive (every mailbox has one), then the email's OWN mailbox's folders (another
  // mailbox's cannot hold it), then Aidey's. Several emails: a mailbox's folders only when every one
  // of them is in that mailbox; Archive only when every one can be archived.
  const destinationsFor = (list) => {
    if (!list.length) return [];
    const conn = list[0].connectionId;
    const oneMailbox = list.every((e) => MAIL_PROVIDERS.includes(e.provider) && e.connectionId === conn);
    const mb = oneMailbox ? mailboxes.find((m) => m.connectionId === conn) : null;
    const own = (mb && Array.isArray(mb.folders) ? mb.folders : [])
      .filter((f) => !list.some((e) => e.folder === 'mailbox' && f.id === e.mailboxFolderId))
      .map((f) => ({ id: `mb:${f.id}`, label: f.name, icon: '🗂️', color: 'pastel-blue', hint: 'In your mailbox', kind: 'mailbox', folder: f }));
    const aidey = visibleFolders.map((f) => ({ id: `ai:${f.id}`, label: f.name, icon: '📁', color: 'pastel-yellow', hint: 'Aidey folder', kind: 'aidey', folder: f }));
    const inFolders = !!mb && list.every((e) => e.folder === 'mailbox');
    return [...(inFolders ? [INBOX_DEST] : []), ...(list.every((e) => archivable(e, activeFolder)) ? [ARCHIVE_DEST] : []), ...own, ...aidey];
  };
  // "+ New folder" in Move to… (2026-10-23): made in the emails' own mailbox, then they move into it.
  // Only when they are all in one mailbox whose folders loaded (the new one must be able to hold them).
  const createFor = (list) => {
    if (!list.length) return undefined;
    const conn = list[0].connectionId;
    if (!list.every((e) => MAIL_PROVIDERS.includes(e.provider) && e.connectionId === conn)) return undefined;
    const mb = mailboxes.find((m) => m.connectionId === conn);
    if (!mb || !Array.isArray(mb.folders)) return undefined;
    return {
      label: '+ New folder', inputLabel: `New folder name in ${mb.email}`, saveLabel: 'Add and move',
      onSave: async (name) => {
        try {
          const data = await (await api('/api/email/mailbox-folders', { method: 'POST', body: JSON.stringify({ connectionId: mb.connectionId, name }) })).json();
          if (!data.folder) return { error: 'That folder was not made. Nothing was moved.' };
          patchMailbox(mb.connectionId, (l) => [...l, data.folder]);
          return { dest: { id: `mb:${data.folder.id}`, label: data.folder.name, kind: 'mailbox', folder: data.folder } };
        } catch (err) {
          return { error: messageFromError(err, 'That folder was not made. Nothing was moved.') };
        }
      },
    };
  };
  const moveDestinations = (email) => (email ? destinationsFor([email]) : []);

  // ── The list pane ──────────────────────────────────────────────────────────

  const rowFor = (email) => (
    <EmailRow key={keyOf(email)} email={email} time={formatDate(email.date)} account={metaFor(email)}
      open={selected?.id === email.id && selected?.provider === email.provider}
      checked={selectedIds.has(keyOf(email))} onToggle={imapOutsideInbox(email) ? undefined : () => handleToggleSelect(keyOf(email))}
      onSelect={handleSelect} onStar={handleStar} onDelete={imapOutsideInbox(email) ? undefined : handleDelete}
      onRestore={activeFolder === 'deleted' ? handleRestore : undefined}
      showStar={email.provider !== 'aidey' && !imapOutsideInbox(email)} />
  );

  let listBody;
  if (activeFolder === 'spam') {
    listBody = (
      <>
        <p className="em-note" role="status">{spamWeek > 0
          ? `Your mailboxes' Junk folders. Aidey moved ${spamWeek} email${spamWeek === 1 ? '' : 's'} here this week. Not spam moves one back to your Inbox.`
          : 'Your mailboxes\' Junk folders. Not spam moves one back to your Inbox.'}</p>
        {!spamError && spamFailed.map((m) => <p key={m.connectionId} className="em-nav__error" role="alert">{`${m.email}: ${m.error}`}</p>)}
        {spamError ? <ErrorState title="Spam did not load" message={spamError} onRetry={fetchSpamEmails} />
          : spamLoading && spamEmails.length === 0 ? <div className="em-pad"><Skeleton lines={4} label="Loading spam" /></div>
            : spamEmails.length === 0 ? (spamFailed.length ? null : <EmptyState title="No spam">Your Junk folders are empty.</EmptyState>)
              : (
                <div role="list" aria-label="Spam">
                  {spamEmails.map((e) => <SpamRow key={e.id} email={e} time={formatDate(e.date)} onNotSpam={handleNotSpam} onConfirm={handleConfirmSpam} />)}
                </div>
              )}
      </>
    );
  } else if (activeFolder === 'drafts') {
    listBody = (
      <>
        <h3 className="em-section">Aidey drafts{aideyDrafts.length ? ` (${aideyDrafts.length})` : ''}</h3>
        {aideyDraftsError ? <ErrorState title="Aidey's drafts did not load" message={aideyDraftsError} onRetry={fetchAideyDrafts} />
          : aideyDrafts.length === 0 ? <p className="em-note em-note--plain">No Aidey drafts yet.</p>
            : (
              <div role="list" aria-label="Aidey drafts">
                {aideyDrafts.map((d) => <AideyDraftRow key={d.id} draft={d} time={formatDate(d.date)} open={selected?.id === d.id} onSelect={setSelected} />)}
              </div>
            )}
        <h3 className="em-section">Drafts in your mailbox{emails.length ? ` (${emails.length})` : ''}</h3>
        {error ? <ErrorState title="Your drafts did not load" message={error} onRetry={retry} />
          : loading && emails.length === 0 ? <div className="em-pad"><Skeleton lines={3} label="Loading drafts" /></div>
            : emails.length === 0 ? <p className="em-note em-note--plain">No drafts in your mailbox.</p>
              : <div role="list" aria-label="Drafts">{emails.map(rowFor)}</div>}
      </>
    );
  } else if (folderError) {
    listBody = <ErrorState title={`${activeAideyFolder?.name || 'This folder'} did not load`} message={folderError} onRetry={() => setFolderReload((n) => n + 1)} />;
  } else if (error && emails.length === 0) {
    listBody = <ErrorState title="Your emails did not load" message={error} onRetry={retry} />;
  } else if ((loading && emails.length === 0) || (activeFolderId && !folderAssignments)) {
    listBody = <div className="em-pad"><Skeleton lines={6} label="Loading emails" /></div>;
  } else if (displayEmails.length === 0) {
    let text = 'No emails here.';
    if (searchQuery) text = `No results for "${searchQuery}".`;
    else if (readFilter === 'unread') text = onePage ? 'Nothing unread here.' : 'Nothing unread on this page.';
    else if (readFilter === 'starred' || activeFolder === 'starred') text = onePage ? 'No starred emails.' : 'No starred emails on this page.';
    else if (triageFilter) text = 'Nothing in this category on this page.';
    listBody = (
      <EmptyState title={text}
        action={searchQuery ? <Button variant="secondary" size="sm" onClick={clearSearch}>Clear search</Button> : undefined} />
    );
  } else {
    listBody = (
      <>
        {error && <Banner tone="problem">This list could not be refreshed: {error}</Banner>}
        <div className="em-selectall">
          <Checkbox checked={allSelected} label="Select all"
            onChange={(on) => setSelectedIds(on ? new Set(displayEmails.map(keyOf)) : new Set())} />
        </div>
        <div role="list" aria-label={viewName}>{displayEmails.map(rowFor)}</div>
      </>
    );
  }

  const showReadFilter = !['spam', 'drafts'].includes(activeFolder);
  const listPane = (
    <section className="em-list" aria-label={`${viewName} emails`}>
      <div className="em-list__tools">
        {layout !== 'three' && (
          <Button variant="tertiary" className="em-folder-btn" aria-haspopup="dialog" onClick={() => setShowFolders(true)}
            aria-label={`Folders and accounts. Showing ${viewName}${accountName ? ` · ${accountName}` : ''}`}>
            <span className="em-folder-btn__title">{viewName}{accountName ? ` · ${accountName}` : ''}</span>
            <span aria-hidden="true">▾</span>
          </Button>
        )}
        {activeFolder !== 'spam' && (
          <SearchInput label="Search emails" placeholder="Search emails…" value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)} autoComplete="off" autoCorrect="off" spellCheck={false} />
        )}
        {showReadFilter && (
          <div className="em-list__filters">
            <SegmentedControl label="Show" value={readFilter} onChange={setReadFilter}
              options={[{ value: 'all', label: 'All' }, { value: 'unread', label: 'Unread' }, { value: 'starred', label: 'Starred' }]} />
            {unreadShown.length > 0 && activeFolder !== 'sent' && activeFolder !== 'deleted' && (
              <button type="button" className="em-link" onClick={() => markRead(unreadShown, true)}>
                {onePage ? 'Mark all read' : 'Mark page read'}
              </button>
            )}
          </div>
        )}
      </div>

      {activeFolder === 'deleted' && (
        <div className="em-trash">
          {activeProvider === 'gmail' ? (
            <p className="em-note em-note--plain">Gmail empties its own Trash after 30 days. Aidey can&apos;t permanently delete Gmail mail.</p>
          ) : emptyTrashBody ? (
            <Button variant="destructive" size="sm" onClick={() => setConfirmEmpty(true)} disabled={trashCount === 0}>
              Empty Trash{trashCount > 0 ? ` (${trashCount})` : ''}
            </Button>
          ) : (
            <p className="em-note em-note--plain">To empty Trash for good, pick one account first. Empty Trash works one account at a time.</p>
          )}
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="em-bulk" role="toolbar" aria-label="Selected emails">
          <span className="em-bulk__count">{selectedIds.size} selected</span>
          {activeFolder === 'deleted' ? (
            // In Trash the action is Restore. No bulk Delete here: permanent delete has the same
            // Gmail-scope limit as Empty Trash, so it is handled per account there.
            <Button variant="secondary" size="sm" onClick={() => { handleRestore(selectedEmailsList); setSelectedIds(new Set()); }}>Restore to Inbox</Button>
          ) : (
            <>
              <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedEmailsList)}>Delete</Button>
              <Button variant="tertiary" size="sm" onClick={() => handleBulkMarkRead(true)}>Mark read</Button>
              <Button variant="tertiary" size="sm" onClick={() => handleBulkMarkRead(false)}>Mark unread</Button>
              {selectedEmailsList.length > 0 && selectedEmailsList.every((e) => archivable(e, activeFolder)) && <Button variant="tertiary" size="sm" onClick={() => handleBulkMove(ARCHIVE_DEST)}>Archive</Button>}
              {destinationsFor(selectedEmailsList).length > 0 && <Button variant="tertiary" size="sm" onClick={() => setMoveOpen(true)}>Move to…</Button>}
            </>
          )}
          <Button variant="tertiary" size="sm" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </div>
      )}

      <div className="em-list__rows" ref={emailListRef}>{listBody}</div>

      {pagination && pagination.pages > 1 && activeFolder !== 'spam' && (
        <div className="em-pager">
          <span className="em-pager__count">{plural(pagination.total, 'email')}</span>
          <span className="em-pager__controls">
            <Button variant="tertiary" size="sm" onClick={() => goPage(currentPage - 1)} disabled={currentPage <= 1}>← Prev</Button>
            <span className="em-pager__at">{currentPage} / {pagination.pages}</span>
            <Button variant="tertiary" size="sm" onClick={() => goPage(currentPage + 1)} disabled={currentPage >= pagination.pages}>Next →</Button>
          </span>
        </div>
      )}
    </section>
  );

  // ── The reading pane ───────────────────────────────────────────────────────

  const isNormal = selected && selected.folder !== 'sent' && selected.provider !== 'aidey';
  const isDeletedView = activeFolder === 'deleted';
  const inboxActions = isNormal && !imapOutsideInbox(selected);   // star, unread, junk, delete act on the Inbox uid
  const canMove = isNormal && !isDeletedView && MAIL_PROVIDERS.includes(selected.provider) && moveDestinations(selected).length > 0;
  const canArchive = isNormal && !isDeletedView && archivable(selected, activeFolder);
  const readingActions = !selected ? [] : [
    isNormal && !isDeletedView && { key: 'reply', node: <Button variant="confirm" size="sm" onClick={() => setShowReply((r) => !r)}>Reply</Button>, label: 'Reply', run: () => setShowReply(true) },
    selected.provider === 'aidey' && { key: 'senddraft', node: <Button variant="confirm" size="sm" onClick={() => { setShowReply(true); setReplyInitialBody(selected.draftBody || ''); setReplyInitialSubject(selected.subject || ''); }}>Send draft</Button> },
    selected.provider !== 'aidey' && { key: 'forward', node: <Button variant="secondary" size="sm" onClick={() => handleForward(selected)}>Forward</Button>, label: 'Forward', run: () => handleForward(selected) },
    inboxActions && { key: 'star', node: <Button variant="tertiary" size="sm" aria-pressed={!!selected.isStarred} onClick={() => handleStar(selected)}>{selected.isStarred ? '★ Starred' : '☆ Star'}</Button>, label: selected.isStarred ? 'Unstar' : 'Star', run: () => handleStar(selected) },
    inboxActions && !isDeletedView && { key: 'unread', node: <Button variant="tertiary" size="sm" onClick={() => handleMarkUnread(selected)}>Mark unread</Button>, label: 'Mark unread', run: () => handleMarkUnread(selected) },
    inboxActions && !isDeletedView && selected.folder !== 'sent' && { key: 'snooze', node: <Button variant="tertiary" size="sm" onClick={() => setSnoozeFor(selected)}>Snooze</Button>, label: 'Snooze', run: () => setSnoozeFor(selected) },
    (selected.folder === 'sent' || activeFolder === 'sent') && { key: 'remind', node: <Button variant="tertiary" size="sm" onClick={() => setRemindFor(selected)}>Remind if no reply</Button>, label: 'Remind if no reply', run: () => setRemindFor(selected) },
    safeUnsubscribeUrl(selected.unsubscribeUrl) && { key: 'unsub', node: <a className="ui-btn ui-btn--tertiary ui-btn--sm" href={safeUnsubscribeUrl(selected.unsubscribeUrl).href} target="_blank" rel="noopener noreferrer">Unsubscribe</a>, label: 'Unsubscribe', run: () => window.open(safeUnsubscribeUrl(selected.unsubscribeUrl).href, '_blank', 'noopener,noreferrer') },
    canArchive && { key: 'archive', node: <Button variant="tertiary" size="sm" onClick={() => archiveEmails([selected])}>Archive</Button>, label: 'Archive', run: () => archiveEmails([selected]) },
    canMove && { key: 'move', node: <Button variant="tertiary" size="sm" onClick={() => setMoveEmail(selected)}>Move to…</Button>, label: 'Move to…', run: () => setMoveEmail(selected) },
    inboxActions && !isDeletedView && { key: 'junk', node: <Button variant="tertiary" size="sm" onClick={() => handleJunk(selected)}>Mark as junk</Button>, label: 'Mark as junk', run: () => handleJunk(selected) },
    inboxActions && !isDeletedView && selected.fromEmail && { key: 'block', node: <Button variant="tertiary" size="sm" onClick={() => setBlockFor(selected)}>Block sender</Button>, label: 'Block sender', run: () => setBlockFor(selected) },
    selected.provider !== 'aidey' && { key: 'ask', node: <Button variant="tertiary" size="sm" onClick={() => handleAskAidey(selected)}>Ask Aidey</Button>, label: 'Ask Aidey', run: () => handleAskAidey(selected) },
    selected.provider !== 'aidey' && !imapOutsideInbox(selected) && (isDeletedView
      ? { key: 'restore', node: <Button variant="secondary" size="sm" onClick={() => handleRestore([selected])}>Restore to Inbox</Button>, label: 'Restore to Inbox', run: () => handleRestore([selected]) }
      : { key: 'delete', node: <Button variant="destructive" size="sm" onClick={() => handleDelete([selected])}>Delete</Button>, label: 'Delete', run: () => handleDelete([selected]), danger: true }),
  ].filter(Boolean);

  const readingBody = selected && (
    <div className="em-read__inner">
      {layout !== 'one' && (
        <div className="em-read__head">
          <h2 className="em-read__subject">{selected.subject || '(no subject)'}</h2>
          <span className="em-pill" style={{ '--em-acc': `var(--${metaFor(selected).color})` }}>{metaFor(selected).short}</span>
        </div>
      )}
      <SenderLine email={selected} account={metaFor(selected)} when={formatDateTime(selected.date)} />
      {layout !== 'one' && <div className="em-actions">{readingActions.map((a) => <React.Fragment key={a.key}>{a.node}</React.Fragment>)}</div>}
      {selected.triageCategory && isNormal && (
        <span className="em-read__triage"><StatusPill tone={selected.triageCategory === 'urgent' ? 'warning' : selected.triageCategory === 'actionable' ? 'pending' : 'neutral'}>
          Aidey sorted this as {selected.triageCategory === 'fyi' ? 'FYI' : selected.triageCategory}
        </StatusPill></span>
      )}
      {isNormal && <AideySummaryCard key={`summary-${selected.id}`} email={selected} onNavigate={goTo} onRun={handleSummaryAction} />}
      {selected.fromEmail && isNormal && (
        <div className="em-read__crm">
          <ContactCard fromEmail={selected.fromEmail} fromName={selected.fromName} onAddToCrm={() => handleAddToCrm(selected)} />
          <SenderAutomation email={selected} configs={automationConfigs}
            onSet={(intent, on) => handleSenderAutomation(selected, intent, on)} />
        </div>
      )}

      {!showReply && isNormal && selected.draftBody && !dismissedDrafts.has(selected.id) && (
        <AideyDraftCard key={selected.id} email={selected} onSend={handleSend} onDismiss={() => handleDismissDraft(selected.id)} sending={replying} />
      )}
      {!showReply && isNormal && !selected.draftBody && !dismissedDrafts.has(selected.id)
        && (selected.triageCategory === 'urgent' || selected.triageCategory === 'actionable') && <AideyDraftPending />}
      {!showReply && isNormal && (selected.triageCategory === 'urgent' || selected.triageCategory === 'actionable')
        && !Object.keys(automationConfigs).some((k) => automationConfigs[k]?.enabled) && (
        <Banner tone="info" action={<Button variant="tertiary" size="sm" onClick={() => gotoSettings('email-automation')}>Set up</Button>}>
          Aidey can automate replies to emails like this.
        </Banner>
      )}

      {selected.possibleSpam && (
        <div data-testid="possible-spam">
          <Banner tone="notice"
            action={<span className="em-actions">
              <Button variant="secondary" size="sm" onClick={() => handlePossibleNotSpam(selected)}>Not spam</Button>
              <Button variant="destructive" size="sm" onClick={() => handlePossibleConfirm(selected)}>Confirm spam</Button>
            </span>}>
            {selected.spamReason || 'Possible spam.'}
          </Banner>
        </div>
      )}
      <div className="em-read__body">
        {selected.provider === 'aidey' ? (
          <p className="em-read__draft">{selected.draftBody || '(empty draft)'}</p>
        ) : (
          selected.bodyError ? <p role="alert" className="em-read__error">{selected.bodyError}</p>
            : selected.body == null ? <p className="em-read__loading" aria-busy="true">Opening the email…</p>
              : <iframe title="Email body" sandbox="" srcDoc={frameDoc(selected.body)} className="em-frame" />
        )}
      </div>

      {showReply && (
        <ReplyComposer key={replyInitialBody || 'manual'} email={selected} onSend={handleSend}
          onClose={() => { setShowReply(false); setReplyInitialBody(''); setReplyInitialSubject(''); }}
          onWriteWithAidey={handleWriteWithAidey} sending={replying}
          initialBody={replyInitialBody} initialSubject={replyInitialSubject || undefined} />
      )}
    </div>
  );

  const readingPane = (
    <section className="em-read" aria-label="Reading pane">
      {selected ? readingBody : (
        <div className="em-read__empty">
          <span className="em-read__empty-tile" aria-hidden="true" />
          <p className="em-read__empty-title">Pick an email to read</p>
          <p className="em-muted">{unreadShown.length ? `You have ${unreadShown.length} unread in this view.` : 'Everything here is read.'}</p>
        </div>
      )}
    </section>
  );

  // Phone: the two main actions at the foot of the full screen email, the rest in ⋯.
  const [firstAction, secondAction, ...moreActions] = readingActions;
  const phoneActions = selected && (
    <>
      {firstAction?.node}
      {secondAction?.node}
      {moreActions.length > 0 && (
        <OverflowMenu label="More actions" title="Email" items={moreActions.map((a) => ({ label: a.label, onSelect: a.run, danger: a.danger }))} />
      )}
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageLayout data-page="emails">
      {header(subline)}

      {/* 2026-10-21: the SAME record and the SAME words as Settings → Email connections
          (lib/mailboxHealth on the server, utils/mailboxHealth.js here). "Your connection may have
          expired" was a guess shown over a mailbox Settings called Connected. Reconnect starts the
          same connect Settings' Reconnect does; an IMAP mailbox is edited in Settings. */}
      {/* 2026-10-22: three kinds, from the actual error (utils/mailboxHealth bannerAction). Try again
          says "Trying again…" while it runs; the banner then goes, or says when it was tried and why. */}
      {failedConnections.map((fc) => (
        <Banner key={fc.connectionId} tone="problem"
          action={bannerAction(fc) === 'retry'
            ? <Button variant="secondary" size="sm" onClick={retryMailboxes} disabled={retrying} aria-busy={retrying || undefined}>{retrying ? 'Trying again…' : 'Try again'}</Button>
            : <Button variant="secondary" size="sm" onClick={() => reconnectMailbox(fc).catch((err) => showToast(messageFromError(err, 'The reconnect could not be started.'), 'error'))}>{fixLabel(fc)}</Button>}>
          {bannerText(fc, { tried: triedAgain })}
        </Banner>
      ))}

      {activeFolder !== 'spam' && activeFolder !== 'drafts' && <TriageChips emails={emails} value={triageFilter} onChange={setTriageFilter} />}

      <div ref={shellRef} className={`em-shell em-shell--${layout}`}>
        {layout === 'three' && <aside className="em-folders">{nav}</aside>}
        {listPane}
        {layout !== 'one' && readingPane}
      </div>

      {layout === 'one' && (
        <SidePanel open={!!selected} onClose={closeReading} title={selected?.subject || '(no subject)'}
          color={selected ? metaFor(selected).color : 'pastel-lilac'} closeLabel="Back to the list" actions={phoneActions}>
          {readingBody}
        </SidePanel>
      )}

      <Sheet open={showFolders && layout !== 'three'} onClose={() => setShowFolders(false)} title="Folders and accounts">{nav}</Sheet>

      <MoveToSheet open={moveOpen} onClose={() => setMoveOpen(false)} title="Move to…"
        itemName={selectedEmailsList.length ? plural(selectedEmailsList.length, 'email') : undefined}
        destinations={destinationsFor(selectedEmailsList)} create={createFor(selectedEmailsList)}
        onMove={(d) => { setMoveOpen(false); handleBulkMove(d); }} />

      <MoveToSheet open={!!snoozeFor} onClose={() => setSnoozeFor(null)} title="Snooze until"
        itemName={snoozeFor ? (snoozeFor.subject || '(no subject)') : undefined}
        destinations={snoozeFor ? snoozeChoices().map((c) => ({ id: c.id, label: c.label, icon: '⏰', color: 'pastel-blue', until: c.until })) : []}
        onMove={(d) => { const email = snoozeFor; setSnoozeFor(null); if (email) handleSnooze(email, d.until); }} />

      <MoveToSheet open={!!remindFor} onClose={() => setRemindFor(null)} title="Remind me if no reply"
        itemName={remindFor ? (remindFor.subject || '(no subject)') : undefined}
        destinations={[{ id: 3, label: 'In 3 days', icon: '🔔', color: 'pastel-yellow' }, { id: 7, label: 'In a week', icon: '🔔', color: 'pastel-yellow' }]}
        onMove={(d) => { const email = remindFor; setRemindFor(null); if (email) handleRemind(email, d.id); }} />

      <SidePanel open={!!cleanup} onClose={() => setCleanup(null)} title="Clean up your inbox" color="pastel-mint">
        {cleanup && (cleanup.status === 'failed' ? (
          <ErrorState message={cleanup.error} onRetry={openCleanup} />
        ) : cleanup.status === 'loading' ? (
          <Skeleton lines={3} label="Looking through your inbox" />
        ) : !cleanup.list.length ? (
          <p className="em-muted">{cleanup.note || 'Nothing to clean up: no sender is filling your inbox with bulk mail.'}</p>
        ) : (
          <ul className="em-cleanup" data-testid="email-cleanup">
            {cleanup.list.map((g) => {
              const u = safeUnsubscribeUrl(g.unsubscribeUrl);
              return (
                <li key={g.sender} className="em-cleanup__row">
                  <span className="em-cleanup__who">{g.name ? `${g.name} · ` : ''}{g.sender}</span>
                  <span className="em-muted">{g.count} emails in your inbox</span>
                  <span className="em-cleanup__actions">
                    {u && <a className="ui-btn ui-btn--tertiary ui-btn--sm" href={u.href} target="_blank" rel="noopener noreferrer" aria-label={`Unsubscribe from ${g.sender}`}>Unsubscribe</a>}
                    <Button variant="tertiary" size="sm" aria-label={`Archive all from ${g.sender}`} onClick={() => archiveSender(g.sender)}>Archive all</Button>
                    <Button variant="tertiary" size="sm" aria-label={`Block ${g.sender}`} onClick={() => setBlockFor({ fromEmail: g.sender })}>Block</Button>
                  </span>
                </li>
              );
            })}
          </ul>
        ))}
      </SidePanel>

      <MoveToSheet open={!!moveEmail} onClose={() => setMoveEmail(null)} title="Move to…"
        itemName={moveEmail ? (moveEmail.subject || '(no subject)') : undefined}
        destinations={moveDestinations(moveEmail)} create={moveEmail ? createFor([moveEmail]) : undefined}
        onMove={(d) => { const email = moveEmail; setMoveEmail(null); if (email) handleMoveEmail(email, d); }} />

      <ConfirmDialog open={!!deletingFolder} onClose={() => setDeletingFolder(null)} onConfirm={handleDeleteMailboxFolder} defaultCancel
        title={deletingFolder?.folder.deletesEmails === false ? 'Delete this label?' : 'Delete this folder and its emails?'}
        confirmLabel={deletingFolder?.folder.deletesEmails === false ? 'Delete label' : 'Delete folder and emails'}
        message={(() => {
          if (!deletingFolder) return '';
          const { folder } = deletingFolder;
          if (folder.deletesEmails === false) return `Delete the label "${folder.name}"? Its emails stay in your mailbox; they just lose this label. This can't be undone.`;
          const n = folder.messageCount;
          const what = n == null ? 'every email in it' : n === 0 ? 'nothing else (it is empty)' : `the ${plural(n, 'email')} in it`;
          return `Delete the folder "${folder.name}" and ${what}? This can't be undone from Aidey.`;
        })()} />

      <ConfirmDialog open={confirmEmpty} onClose={() => setConfirmEmpty(false)} onConfirm={handleEmptyTrash}
        title="Empty Trash for good?" confirmLabel="Empty Trash"
        message={`This permanently deletes ${plural(trashCount, 'email')} from ${accountName || 'this account'}'s Trash. It can't be undone.`} />

      <ConfirmDialog open={!!blockFor} onClose={() => setBlockFor(null)} onConfirm={handleBlock}
        title="Block this sender?" confirmLabel="Block"
        message={blockFor ? `Block ${blockFor.fromEmail}? Their emails will go to Junk, and Aidey won't act on them. You can unblock them in Settings > Email connections.` : ''} />

      <ConfirmDialog open={!!noTrash} onClose={() => setNoTrash(null)} onConfirm={handleDeleteForGood}
        title="Delete for good?" confirmLabel="Delete anyway" defaultCancel
        message={noTrash?.question} />

      {compose && (
        <ComposeModal connections={activeConnections} onSend={handleCompose} onClose={() => setCompose(null)}
          sending={composing} onImprove={improveDraft} initial={compose} />
      )}
    </PageLayout>
  );
}
